/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React from 'react';

// Importing helper and component modules
import { permissionObj } from '../../Helpers/permission';
import ExportDataBtn from '../../components/buttons/ExportDataBtn';
import CreateCardBtn from '../../components/buttons/CreateCardBtn';
import CreationErrorsModal from '../Modals/CreationErrorsModal';
import AssignCardModal from '../Dashboard/Company/Employees/Modal/AssignCardModal';
import AddTemplate from '../../components/buttons/AddTemplate';
import CardPreview from './CardPreview';
import CardsSectionTable from "./CardsSectionTable";
import HeadTittle from "../../components/HeadTittle";
import CardsPanelApp from "./Functionality/CardsPanelApp";
import ButtonAction from "../../components/buttons/ButtonAction";
import DisassignTemplateModal from "./Modal/DisassignTemplateModal";
import {TrashOutlineIcon} from '../../components/buttons/IconButtons';
import DeleteModal from "../Modals/DeleteModal";
import ConfirmModal from "../Dashboard/Visitor/Invitation/Modal/ConfirmModal";

// Main component for managing cards
const CardsPanel = () => {
    // Destructuring all state variables and functions from the custom hook
    const {
        t,
        DEV,
        permission,
        modified,
        setModified,
        search,
        setSearch,
        moduleId,
        option,
        bodyForExport,
        setBodyForExport,
        searchElement,
        setSearchElement,
        creationErrorsShow,
        setCreationErrorsShow,
        selectedCards,
        setSelectedCards,
        deleteCardShow,
        setDeleteCardShow,
        isAllChecked,
        setIsAllChecked,
        simpleSearch,
        setSimpleSearch,
        unassignModalShow,
        setUnassignModalShow,
        previewShow,
        setPreviewShow,
        assignCardShow,
        setAssignCardShow,
        disassignCardShow,
        setDisassignCardShow,
        cardToUnassign,
        cardToAssign,
        currentId,
        results,
        setResults,
        unassignCard,
        deleteSelectedCard,
        onAssignUnassign,
        handlePreview,
        onDisassignTemplate
    } = CardsPanelApp();

    return (
        <>
            {/* Page title and action buttons */}
            <HeadTittle name={'card_management'}>
                {/* Conditionally render export button based on permissions */}
                {(DEV || permission?.includes(permissionObj?.WEB_CARD_EXPORT)) &&
                <>
                        <ExportDataBtn
                            id="btn_cp_export"
                            moduleId={moduleId}
                            body={bodyForExport}
                            option={option}
                            search={search}
                            searchElement={searchElement}
                        />
                </>
                }

                {/* Render card creation button if permitted */}
                {(DEV || permission?.includes(permissionObj?.WEB_CARD_CREATE)) && (
                    <CreateCardBtn
                        setModified={setModified}
                        setCreationErrorsShow={setCreationErrorsShow}
                    />
                )}

                {/* Render "Add Template" button if permitted */}
                {(DEV || permission?.includes(permissionObj?.WEB_CARD_MANAGE_TEMPLATES)) && (
                    <AddTemplate
                        selectedCards={selectedCards}
                        setSelectedCards={setSelectedCards}
                        setModified={setModified}
                        results={results}
                    />
                )}

                {/* Render delete button if permitted */}
                {(DEV || permission?.includes(permissionObj?.WEB_CARD_DELETE)) && (
                    <ButtonAction
                        id='btn_cp_delete'
                        name='delete'
                        className='btn-icon-delete'
                        disabled={selectedCards?.length === 0}
                        Icon={TrashOutlineIcon}
                        onClick={() => setDeleteCardShow(true)}
                    />
                )}
            </HeadTittle>

            {/* Table section for displaying cards */}
            <CardsSectionTable
                isAllChecked={isAllChecked}
                setIsAllChecked={setIsAllChecked}
                selectedList={selectedCards}
                setSelectedList={setSelectedCards}
                setBodyForExport={setBodyForExport}
                searchElement={searchElement}
                setSearchElement={setSearchElement}
                optionTable={option}
                search={search}
                setSearch={setSearch}
                simpleSearch={simpleSearch}
                setSimpleSearch={setSimpleSearch}
                onAssignUnassign={onAssignUnassign}
                onPreview={handlePreview}
                onDisassignTemplate={onDisassignTemplate}
                modified={modified}
                setModified={setModified}
                results={results}
                setResults={setResults}
            />

            {/* Modal to confirm disassigning an employee */}
            <ConfirmModal
                show={unassignModalShow}
                onHide={() => setUnassignModalShow(false)}
                onClickFn={() => {
                    unassignCard(cardToUnassign)
                    setUnassignModalShow(false);
                }}
                title_modal={'disassign_employee'}
                description={t("are_you_sure_you_want_to_disassign_employee")}
            />

            {/* Modal to confirm deleting selected cards */}
            <DeleteModal
                show={deleteCardShow}
                onHide={() => setDeleteCardShow(false)}
                onClickFn={() => deleteSelectedCard(selectedCards)}
                data={selectedCards}
                title_modal={t('delete_cards')}
                description={'are_you_sure_you_want_to_delete'}
                element_modal={t('cards')}
            />

            {/* Modal showing creation errors */}
            {creationErrorsShow &&
            <CreationErrorsModal
                show={creationErrorsShow}
                onHide={() => setCreationErrorsShow(false)}
                cards={true}
            />
            }

            {/* Modal for assigning a card to an employee */}
            {assignCardShow &&
            <AssignCardModal
                open={assignCardShow}
                cardToAssign={cardToAssign}
                onHide={() => setAssignCardShow(false)}
                setModified={setModified}
            />
            }

            {/* Modal for previewing a selected card */}
            {previewShow && (
                <CardPreview
                    open={previewShow}
                    currentId={currentId}
                    onHide={() => setPreviewShow(false)}
                />
            )}

            {/* Modal for disassigning a template from a card */}
            {disassignCardShow &&
            <DisassignTemplateModal
                show={disassignCardShow}
                onHide={() => setDisassignCardShow(false)}
                selectedCards={[currentId]}
                setModified={setModified}
            />
            }
        </>
    );
};

export default CardsPanel; // Exporting the component
